import React, { useState } from "react";
import { useSettings } from "../../context/SettingsContext";
import VoteBarChart from "../VoteCharts/VoteBarChart";
import PlayerRoundVoteBreakdown from "./PlayerRoundVoteBreakdown";
import PlayerVoteProgressionChart from "./PlayerVoteProgressionChart";
import { formatPct } from "../../utils/deadHeat";
import ImpliedOddsInfo from "../common/ImpliedOddsInfo";

// One cell of the compact metrics strip: tiny mono eyebrow over a dense numeral.
const MetricCell = ({ label, children }) => (
  <div className="bg-slate-950/70 px-3 py-2 sm:py-2.5">
    <div className="text-[9px] font-mono uppercase tracking-[0.12em] text-slate-500 leading-none whitespace-nowrap">{label}</div>
    <div className="text-sm sm:text-[15px] font-mono font-bold tabular-nums text-white leading-none mt-1.5 whitespace-nowrap">
      {children}
    </div>
  </div>
);

// Mono eyebrow used as a section divider inside the modal body.
const SectionHeading = ({ children }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">{children}</span>
    <div className="h-px flex-1 bg-slate-800/70" />
  </div>
);

const DetailedPlayerVoteModal = ({ isOpen, onClose, player, playerImage, gameVoteData }) => {
  const [currentThreshold, setCurrentThreshold] = useState(null);
  const { getEnabledKeys } = useSettings();
  const enabledKeys = getEnabledKeys();

  if (!isOpen || !player) return null;

  // Calculate vote range from only enabled datasets (consistent with table filtering)
  const allPairs = [
    { k: 'count1', label: 'BG 1', val: player.count1 },
    { k: 'count2', label: 'BG 2', val: player.count2 },
    { k: 'count3', label: 'BG 3', val: player.count3 },
    { k: 'count4', label: 'BG 4', val: player.count4 },
    { k: 'count5', label: 'BG 5', val: player.count5 },
    { k: 'betfair', label: 'Betfair', val: player.betfair },
    { k: 'espn', label: 'ESPN', val: player.espn },
    { k: 'byb', label: 'BYB', val: player.byb },
  ];
  const activePairs = allPairs.filter(p => enabledKeys.includes(p.k));
  const activeCounts = activePairs.map(p => Number(p.val ?? 0));
  const minVotes = activeCounts.length ? Math.min(...activeCounts) : 0;
  const maxVotes = activeCounts.length ? Math.max(...activeCounts) : 0;

  // Prepare data for the chart (enabled only)
  const chartCounts = activePairs.map(p => p.val);
  const chartLabels = activePairs.map(p => p.label);

  // Handle threshold changes from the bar chart
  const handleThresholdChange = (threshold) => {
    setCurrentThreshold(threshold);
  };

  // Calculate hit rate and implied odds
  const calculateHitRate = () => {
    if (currentThreshold === null) return { hitCount: 0, totalCount: 8, percentage: 0 };

    const hitCount = chartCounts.filter(count => count > currentThreshold).length;
    const totalCount = chartCounts.length || 1;
    const percentage = (hitCount / totalCount) * 100;

    return { hitCount, totalCount, percentage };
  };

  const calculateImpliedOdds = (percentage) => {
    if (percentage === 0) return "N/A";
    return `$${(100 / percentage).toFixed(2)}`;
  };

  const hitRateData = calculateHitRate();
  const impliedOdds = calculateImpliedOdds(hitRateData.percentage);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-[#0f172a] rounded-2xl border border-slate-700/60 shadow-2xl shadow-black/60 w-full max-w-lg sm:max-w-2xl h-[80vh] max-h-[80vh] md:h-[94vh] md:max-h-[94vh] my-4 sm:my-8 relative flex flex-col overflow-hidden">
        {/* Sticky header: emerald accent, player identity, close button */}
        <div className="flex-shrink-0 border-b border-slate-800 bg-[#0f172a]">
          <div className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4">
            <span className="w-1 self-stretch rounded-full bg-emerald-500/70 flex-shrink-0" aria-hidden="true" />
            {/* Player image */}
            <div className="flex-shrink-0">
              {playerImage ? (
                <img
                  src={playerImage}
                  alt={player.name}
                  className="w-11 h-11 sm:w-14 sm:h-14 rounded-full object-cover ring-1 ring-slate-700/60"
                />
              ) : (
                <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center ring-1 ring-slate-700/60">
                  <span className="text-white font-bold text-sm sm:text-base">{player.image}</span>
                </div>
              )}
            </div>
            {/* Player details */}
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-xl font-semibold text-white tracking-tight truncate">{player.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">{player.team}</span>
                <span className="text-slate-700">•</span>
                <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.1em] text-slate-500 whitespace-nowrap">Position #{player.position}</span>
              </div>
            </div>
            {/* Close button */}
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md border border-slate-700/60 bg-slate-800/40 text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto styled-scrollbar">
          <div className="p-4 sm:p-6">
            {/* Compact metrics strip: all four figures in one dense band. Hit rate
                and implied odds react live to the threshold slider in the chart. */}
            <div className="rounded-xl border border-slate-800 overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-800/60">
                <MetricCell label="Projected Votes">
                  <span className="text-emerald-300">
                    {(() => {
                      const n = chartCounts.length || 1;
                      const sum = chartCounts.reduce((a, b) => a + (Number(b) || 0), 0);
                      return (sum / n).toFixed(2);
                    })()}
                  </span>
                </MetricCell>
                <MetricCell label="Vote Range">
                  {minVotes} - {maxVotes}
                </MetricCell>
                <MetricCell label="Hit Rate">
                  {hitRateData.hitCount}/{hitRateData.totalCount} <span className="text-slate-400">({formatPct(hitRateData.percentage)}%)</span>
                </MetricCell>
                <MetricCell label={<span className="inline-flex items-center">Implied Odds<ImpliedOddsInfo /></span>}>
                  <span className="text-emerald-300">{impliedOdds}</span>
                </MetricCell>
              </div>
            </div>

            {/* Vote bar chart */}
            <div className="mt-5 sm:mt-6">
              <SectionHeading>Vote Visualization</SectionHeading>
              <VoteBarChart
                counts={chartCounts}
                labels={chartLabels}
                onThresholdChange={handleThresholdChange}
              />
            </div>

            {/* Round vote breakdown */}
            <div className="mt-5 sm:mt-6">
              <SectionHeading>Round Vote Breakdown</SectionHeading>
              <PlayerRoundVoteBreakdown
                player={player}
                gameVoteData={gameVoteData}
                enabledKeys={enabledKeys}
              />
            </div>

            {/* Cumulative season progression */}
            <div className="mt-5 sm:mt-6">
              <SectionHeading>Vote Progression</SectionHeading>
              <PlayerVoteProgressionChart
                player={player}
                gameVoteData={gameVoteData}
                enabledKeys={enabledKeys}
              />
            </div>

            {/* Mobile-specific spacing at bottom */}
            <div className="h-4 sm:h-0"></div>
          </div>
        </div>

        {/* Sticky Footer (mobile) */}
        <div className="flex-shrink-0 bg-[#0f172a] border-t border-slate-800 sm:hidden">
          <div className="p-3 grid grid-cols-1 gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg bg-emerald-500 text-slate-950 text-sm font-semibold hover:bg-emerald-400 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedPlayerVoteModal;
